import React, { ReactElement } from "react";
import {
  ListItem as MuiListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Grid,
  Typography,
  Chip,
  makeStyles,
} from "@material-ui/core";
import { SvgIconComponent } from "@material-ui/icons";

type ChipDetails = {
  text: string;
  colour?: string | null;
  icon?: ReactElement | null;
};

type ListItemProps = {
  onClick: () => void;
  dataTest?: string;
  Icon: SvgIconComponent;
  primaryText: string;
  reference?: string;
  secondaryDetails: ListItemSecondaryDetail[];
  chipTexts: ChipDetails[] | string[];
  [key: string]: any;
};

export type ListItemSecondaryDetail = {
  heading: string;
  value: string | null | undefined;
};

const useStyles = makeStyles((theme) => ({
  rowDetails: {
    paddingRight: "20px",
  },
  chip: {
    float: "right",
    margin: "0.1em",
  },
  chipsContainer: {
    float: "right",
  },
  primaryText: {
    lineHeight: "1.43",
    fontSize: "0.875rem",
    letterSpacing: "0.01071em",
  }
}));

export const ListItem: React.FC<ListItemProps> = (props: ListItemProps) => {
  const {
    onClick,
    dataTest,
    Icon,
    primaryText,
    secondaryDetails,
    chipTexts,
    reference,
    ...otherProps
  } = props;

  const classes = useStyles();

  return (
    <MuiListItem
      button
      alignItems="flex-start"
      onClick={onClick}
      divider
      data-test={dataTest}
      {...otherProps}
    >
      <ListItemAvatar>
        <Avatar>
          <Icon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Grid
            container
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item xs={12} lg={11} container direction="column">
              <Grid item>
                {!!reference && (
                  <>
                    <Typography
                      component="span"
                      // color="textSecondary"
                      data-test="reference-number"
                      className={classes.primaryText}
                    >
                      {reference}
                    </Typography>
                    &nbsp;&nbsp;
                  </>
                )}
                <Typography
                  component="span"
                  variant="h6"
                  color="textPrimary"
                  className={classes.primaryText}
                >
                  {primaryText}
                </Typography>
              </Grid>
              <Grid item container>
                {secondaryDetails
                  .filter((x) => !!x.value)
                  .map((x) => (
                    <Grid item key={x.heading}>
                      <Typography
                        className={classes.rowDetails}
                        component="span"
                        variant="body2"
                        color="textPrimary"
                      >
                        <strong>{x.heading}:</strong> {x.value}
                      </Typography>
                    </Grid>
                  ))}
              </Grid>
            </Grid>
            <Grid item xs={12} lg={1}>
              <div className={classes.chipsContainer}>
                {chipTexts.map((x) => (
                  <Chip
                    style={{
                      backgroundColor: `${typeof x === "object" && x.colour}`,
                    }}
                    key={typeof x === "string" ? x : x.text}
                    icon={
                      <>
                        {typeof x === "object"
                          ? x.icon
                            ? x.icon
                            : undefined
                          : undefined}
                      </>
                    }
                    size="small"
                    label={typeof x === "string" ? x : x.text}
                    color="secondary"
                    className={classes.chip}
                    data-test="row-chip"
                  />
                ))}
              </div>
            </Grid>
          </Grid>
        }
      />
    </MuiListItem>
  );
};
